import { isNil, isNull } from 'lodash-es';
import { useCallback } from 'react';

import { useOverlay } from './contexts/OverlayContext.js';
import { useTooltip } from './contexts/TooltipContext.js';
import type { TooltipAction } from './types.js';
import { emptyData } from './utils.js';

/** @internal */
export const useTooltipReducer = () => {
  const store = useTooltip();
  const overlay = useOverlay();

  const dispatch = useCallback(
    (action: TooltipAction) => () => {
      switch (action.type) {
        case 'RESET_TOOLTIP':
          overlay.clear();

          store.setState(() => ({
            pinned: false,
            absoluteX: null,
            absoluteY: null,
            relativeX: null,
            relativeY: null,
            percentX: null,
            percentY: null,
            inBounds: false,
            activeSeries: null,
            data: emptyData(),
          }));
          break;
        case 'SET_PINNED':
          store.setState((prevState) => {
            prevState.inBounds || overlay.clear();
            const { pinned, data, absoluteX, absoluteY } = action.payload;
            const activeSeries = pinned ? prevState.activeSeries : null;

            if (isNil(absoluteX) || isNil(absoluteY)) {
              return {
                ...prevState,
                inBounds: true,
                pinned,
                data,
                activeSeries,
              };
            }

            const positions = overlay.getAdjustedPositionsFromAbsolute(
              absoluteX,
              absoluteY,
            );

            return {
              ...prevState,
              pinned,
              inBounds: true,
              data,
              activeSeries,
              ...positions,
            };
          });
          break;
        case 'SET_POSITION':
          store.setState((prevState) => {
            if (prevState.pinned) {
              return prevState;
            }
            prevState.inBounds || overlay.clear();
            const { absoluteX, absoluteY, data } = action.payload;
            const positions = overlay.getAdjustedPositionsFromAbsolute(
              absoluteX,
              absoluteY,
            );

            return {
              ...prevState,
              inBounds: true,
              ...positions,
              data: data || emptyData(),
            };
          });
          break;
        case 'SET_OUT_BOUNDS':
          store.setState((prevState) => {
            overlay.clear();

            if (prevState.pinned) {
              return prevState;
            }

            return {
              ...prevState,
              absoluteX: null,
              absoluteY: null,
              relativeX: null,
              relativeY: null,
              percentX: null,
              percentY: null,
              inBounds: false,
              data: emptyData(),
            };
          });
          break;
        case 'UPDATE_POSITION':
          store.setState((prevState) => {
            overlay.clear();

            const { pinned, percentX, percentY, relativeX, relativeY } =
              prevState;
            if (!pinned || isNil(percentX) || isNil(percentY)) {
              return prevState;
            }

            const positions = overlay.getAdjustedPositionsFromPercent(
              percentX,
              percentY,
              ...(action.payload.type === 'non-resize'
                ? [relativeX as number, relativeY as number]
                : [undefined, undefined]),
            );

            return {
              ...prevState,
              ...positions,
              activeSeries: action.payload.scroll
                ? null
                : prevState.activeSeries,
            };
          });
          break;
        case 'SET_ACTIVE_SERIES':
          store.setState((prevState) => {
            if (prevState.activeSeries === action.payload.activeSeries) {
              return prevState;
            }
            return {
              ...prevState,
              activeSeries: action.payload.activeSeries,
            };
          });
          break;
        case 'TOOLTIP_MOUSE_LEAVE':
          store.setState((prevState) => {
            if (isNull(prevState.activeSeries)) {
              return prevState;
            }

            return {
              ...prevState,
              activeSeries: null,
            };
          });
          break;
        default:
          throw new Error('Unhandled action type');
      }
    },
    [store, overlay],
  );

  return dispatch;
};
